import React, { useMemo, useState } from 'react'
import Nestable from 'react-nestable'

import { Icon, Select, Typography } from 'antd'

import EmptyOptions from '../../../../../../assets/images/emptyOptions.png'
import SelectIcon from '../../../../../../assets/images/select.png'
import { getMessage } from '../../../../../../util/IntlMessages'
import { useSelectOptionsContext } from '../../context/selectOptions'
import { useManageFieldRelationships } from '../../hooks/useManageFieldRelationships'
import { useManageField } from '../../hooks/useManageFields'
import { useManageSelectOptions } from '../../hooks/useManageSelectOptions'
import { SelectOption } from '../SelectOption'
import { RemoveRelationshipsModal } from '../RemoveRelationshipsModal'

import './styles.less'

export function SelectOptions() {
  const [relationshipsModal, setRelationshipsModal] = useState(null)

  const { options, fields, relatedFieldIds } = useSelectOptionsContext()

  const { currentField, selectedFieldId, getFieldById } = useManageField()

  const {
    addFieldRelationship,
    removeFieldRelationship,
    isLoading: isFieldRelationshipsLoading
  } = useManageFieldRelationships({ fieldId: selectedFieldId })

  const { isLoading, createOption } = useManageSelectOptions()

  function handleFieldRelationship({ relatedFieldId, type, mainFieldId }) {
    if (type === 'REMOVE') {
      setRelationshipsModal({
        onOk: () => {
          setRelationshipsModal(null)
          removeFieldRelationship({ relatedFieldId, mainFieldId })
        },
        message: getMessage('removeFieldRelated', getFieldById(relatedFieldId).fieldName)
      })
    } else {
      addFieldRelationship({ relatedFieldId, mainFieldId })
    }
  }

  const optionsList = useMemo(() => options && options.ids.map((id) => ({ id })), [options?.ids])

  const selectFields = useMemo(() => {
    const currentGroup = currentField.fieldGroup
    return fields?.ids.reduce((acc, id) => {
      const field = fields.entities[id]
      if (
        field.active &&
        field.fieldGroup === currentGroup &&
        field.internalTypeName === 'select' &&
        field.id !== selectedFieldId
      ) {
        acc.push(field)
      }
      return acc
    }, [])
  }, [currentField])

  return (
    <>
      <div className="selectOptions">
        <RemoveRelationshipsModal
          visible={!!relationshipsModal}
          message={relationshipsModal?.message}
          onCancel={() => setRelationshipsModal(null)}
          onOk={relationshipsModal?.onOk}
        />
        <div className="selectOptions__header">
          <Typography.Text className="selectOptions__headerTitle">{getMessage('newOption')}</Typography.Text>
          <Icon
            type="plus-square"
            className="selectOptions__headerIcon"
            onClick={() => createOption(selectedFieldId)}
          />
        </div>

        <div className="selectOptions__horizontalLine" />

        <div className="selectOptions__wrapper">
          {isLoading && <Loading />}

          {options?.ids.length === 0 && (
            <div className="selectOptions__emptyOptions">
              <img alt="empty-options" src={EmptyOptions} />
              <Typography.Text>{'Adicione opções à lista'}</Typography.Text>
            </div>
          )}

          {optionsList?.length > 0 && (
            <Nestable
              disableDrag
              items={optionsList}
              maxDepth={0}
              idProp="id"
              renderItem={({ item }) => <SelectOption optionId={item?.id} selectedFieldId={selectedFieldId} />}
            />
          )}
        </div>
      </div>

      {selectFields?.length > 0 && (
        <div className="selectOptions__relationshipsWrapper">
          <Typography.Text>{getMessage('customFieldRelationships')}</Typography.Text>
          <Select
            loading={isFieldRelationshipsLoading}
            mode="multiple"
            onSelect={(relatedFieldId) =>
              handleFieldRelationship({ relatedFieldId, type: 'ADD', mainFieldId: selectedFieldId })
            }
            onDeselect={(relatedFieldId) =>
              handleFieldRelationship({ relatedFieldId, type: 'REMOVE', mainFieldId: selectedFieldId })
            }
            value={relatedFieldIds}
            filterOption={(input, option) => option.props.label.toLowerCase().includes(input.toLowerCase())}
          >
            {selectFields.map(({ id }) => {
              const field = fields.entities[id]
              return (
                <Select.Option value={field.id} key={field.id} label={field.fieldName}>
                  <img src={SelectIcon} alt="select-icon" className="selectOptions__selectIcon" />
                  {field.fieldName}
                </Select.Option>
              )
            })}
          </Select>
        </div>
      )}
    </>
  )
}

const Loading = () => (
  <div className="selectOptions__loadingWrapper">
    <Icon type="loading" className="selectOptions__loadingIcon" spin />
    <Typography className="selectOptions__loadingText">{getMessage('loading...')}</Typography>
  </div>
)
